import type { AppUserInfo } from '@/models/AppViewModel';

export type AppUserInfoAdvisorGroupFields = AppUserInfo & {
  AdvisorGroupId?: number | null;
  AdvisorGroup?: { AdvisorGroupId?: number | null } | null;
  AGSettings?: Array<{ AdvisorGroupId?: number | null }> | null;
  LogoUrl?: string | null;
  MobileIconUrl?: string | null;
};

/**
 * Derive the authenticated user's advisorGroupId from common payload shapes.
 * Returns null when not available.
 */
export function deriveAdvisorGroupId(
  userInfo: AppUserInfo | undefined,
): number | null {
  const ui = userInfo as AppUserInfoAdvisorGroupFields | undefined;

  const direct = ui?.AdvisorGroupId;
  if (typeof direct === 'number' && Number.isFinite(direct)) {
    return direct;
  }

  const nested = ui?.AdvisorGroup?.AdvisorGroupId;
  if (typeof nested === 'number' && Number.isFinite(nested)) {
    return nested;
  }

  const viaAgSettings = ui?.AGSettings?.map((s) => s.AdvisorGroupId).find(
    (v): v is number => typeof v === 'number' && Number.isFinite(v),
  );
  if (typeof viaAgSettings === 'number') {
    return viaAgSettings;
  }

  // As a last resort, some payloads include logo URLs that embed the AdvisorGroupId:
  // e.g. ".../Logos/27-ImagePath-<guid>.png"
  const parseFromLogoUrl = (
    value: string | null | undefined,
  ): number | null => {
    if (!value) return null;
    const match = value.match(/\/logos\/(\d+)-/i);
    if (!match?.[1]) return null;
    const parsed = Number.parseInt(match[1], 10);
    return Number.isFinite(parsed) ? parsed : null;
  };

  return parseFromLogoUrl(ui?.LogoUrl) ?? parseFromLogoUrl(ui?.MobileIconUrl);
}
